/*!
  Pioneer WFRIA Step v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["Step"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("Step") < 0) {
    window.__PIONEER_GLOBAL__.components.push("Step");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["className", "status", "defaultStepLabel", "componentStrings", "orientation", "stepLabel", "stepLabelClassNames", "stepValue", "completedStatusIcon", "activeStatusIcon", "prevStepStatus"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import { invalidIf } from "../common/proptypes";
import extractTheme from "./extractTheme";
import { IconConfirmation, IconEdit, IconError, IconImpairment } from "..";
/**
 * Step is a React component that displays a formatted `<li>` which indicates
 * the state of a Step within the `ProgressStepper` component.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function Step(_ref) {
  var _theme$components;

  let {
    className,
    status,
    defaultStepLabel,
    componentStrings,
    orientation,
    // eslint-disable-line react/prop-types
    stepLabel,
    stepLabelClassNames,
    stepValue,
    completedStatusIcon,
    activeStatusIcon,
    prevStepStatus
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    cssRoot,
    cssWrapper,
    cssOuter,
    cssInner,
    cssLabelContainer,
    cssRailContainer,
    cssStepRail,
    cssCompletedStatusIcon
  } = extractTheme(_objectSpread({
    theme,
    status,
    orientation,
    activeStatusIcon
  }, otherProps));
  const ariaCurrent = status === "active" ? {
    "aria-current": true
  } : null;
  const stepClassName = clsx({
    [`pnr-step--${status}`]: !activeStatusIcon,
    "pnr-step--active-icon": activeStatusIcon
  }, {
    className
  });
  const IconTYPES = {
    confirmation: _jsx(IconConfirmation, {
      size: "small"
    }),
    edit: _jsx(IconEdit, {
      size: "small",
      theme: "information"
    }),
    error: _jsx(IconError, {
      size: "small"
    }),
    warning: _jsx(IconImpairment, {
      size: "small"
    })
  };
  const iconStep = Boolean(completedStatusIcon || activeStatusIcon);
  const iconsAllowed = !(theme !== null && theme !== void 0 && (_theme$components = theme.components) !== null && _theme$components !== void 0 && _theme$components.step.modifiers.hasOwnProperty("iconsAllowed"));
  const railClassName = clsx({
    "pnr-step-rail--left-margin": prevStepStatus === "active"
  });
  const {
    stepStatus
  } = componentStrings;
  return _jsxs(React.Fragment, {
    children: [orientation === "vertical" && stepValue !== 1 && _jsx("li", {
      "aria-hidden": "true",
      className: "pnr-step__rail-container",
      css: cssRailContainer,
      children: _jsx("div", {
        className: "pnr-step__rail",
        css: cssStepRail
      })
    }), _jsxs("li", _objectSpread(_objectSpread({}, ariaCurrent), {}, {
      css: cssRoot,
      className: stepClassName,
      children: [orientation === "horizontal" && stepValue !== 1 && _jsx("div", {
        css: cssRailContainer,
        className: railClassName,
        "aria-hidden": "true",
        children: _jsx("span", {
          css: cssStepRail
        })
      }), _jsx("div", {
        css: cssWrapper,
        children: _jsxs("span", {
          className: "pnr-step-container__visual-outer-step",
          css: cssOuter,
          "aria-hidden": !iconStep,
          children: [iconsAllowed && completedStatusIcon && _jsx("span", {
            className: `pnr-step__icon--${completedStatusIcon}`,
            css: cssCompletedStatusIcon,
            children: IconTYPES[completedStatusIcon]
          }), (!completedStatusIcon && !activeStatusIcon || iconStep && !iconsAllowed) && _jsx("span", {
            css: cssInner
          }), iconsAllowed && activeStatusIcon && _jsx("span", {
            className: `pnr-step__icon--${activeStatusIcon}`,
            css: cssCompletedStatusIcon,
            children: IconTYPES.edit
          })]
        })
      }), _jsxs("span", {
        css: cssLabelContainer
        /**
         * role="text" is the undocumented, and not officially supported, approach to help
         * Safari Voiceover properly read the nested text.
         */
        ,
        role: "text" // eslint-disable-line
        ,
        children: [stepLabel ? _jsxs(React.Fragment, {
          children: [_jsxs("span", {
            className: "wf-u-visually-hidden",
            children: [defaultStepLabel, ", "]
          }), _jsx("span", {
            className: stepLabelClassNames,
            children: stepLabel
          })]
        }) : _jsx("span", {
          className: stepLabelClassNames,
          children: defaultStepLabel
        }), _jsx("span", {
          className: "wf-u-visually-hidden",
          children: `, ${stepStatus[status]}`
        }), " "]
      })]
    }))]
  });
}

Step.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Provides configuration of icon instead of Step numbers for active step.
   * Accepts 'active' value only.
   * Invalid when used in Step with 'completed' or 'pending' status.
   */
  activeStatusIcon: invalidIf(PropTypes.oneOf(["active"]), function (props, propName) {
    return props[propName] && props.status !== "active";
  }, "when step is not active."),

  /**
   * Custom class name to include at the top level
   * of the component.
   */
  className: PropTypes.string,

  /**
   * Provides configuration of icon instead of Step numbers for completed steps.
   * Accepts one of 'confirmation', 'error' or 'warning' values only.
   * Invalid when used in Step with 'active' status.
   */
  completedStatusIcon: invalidIf(PropTypes.oneOf(["confirmation", "error", "warning"]), function (props, propName) {
    return props[propName] && props.status !== "completed";
  }, "when step is not completed."),

  /**
   * Default strings passed from the ProgressStepper component.
   * These strings can be altered via `customStrings` in the
   * ProgressStepper API.
   *
   */
  componentStrings: PropTypes.shape({
    stepStatus: PropTypes.shape({})
  }),

  /**
   * The default label associated with the Step.
   */
  defaultStepLabel: PropTypes.string,

  /**
   * The status of the previous step. This is required
   * to determine the gap between the dot and the line.
   * If the previous step is active then margin has to
   * be 20px else it has to be 16px.
   */
  prevStepStatus: PropTypes.string,

  /**
   * The status associated with the given Step.
   */
  status: PropTypes.oneOf(["active", "completed", "pending"]),

  /**
   * The label to be displayed below (horizontal) or to the right (vertical)
   * of the actual Step value.
   */
  stepLabel: PropTypes.string,

  /**
   * Class, or space-separated classes, to add to the label. Useful when combined
   * with [visibility classes](#/CSS/Utilities?id=visibility). Examples can be
   * found in the [ProgressStepper](#/Components/ProgressStepper).
   */
  stepLabelClassNames: PropTypes.string,

  /**
   * The value shown within the Step. Design recommends using numeric values.
   * This is passed from the ProgressStepper.
   */
  stepValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
} : {};
Step.defaultProps = {
  activeStatusIcon: null,
  className: null,
  completedStatusIcon: null,
  componentStrings: null,
  defaultStepLabel: null,
  status: "pending",
  stepLabel: null,
  stepLabelClassNames: null,
  stepValue: null,
  prevStepStatus: null
};
export default Step;